/**
 * Pathname → route metadata entry.
 *
 * Matching order: exact path, then param patterns ("/verify/:token"), then
 * prefix rules ("/superadmin/*"). Case-sensitive, because React Router's
 * routes in this app are (e.g. "/CollegeFAQs"). Trailing slashes are ignored
 * so "/about/" resolves to "/about" and canonicalises to it.
 *
 * Pure functions, no DOM — shared by the runtime and the build scripts.
 */
import { ROUTES, UNMATCHED } from './routes.config';
import { SITE_URL } from './siteConfig';

export function normalizePath(pathname) {
  if (!pathname) return '/';
  let p = String(pathname).split('?')[0].split('#')[0];
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  if (!p.startsWith('/')) p = `/${p}`;
  return p || '/';
}

function matchesPattern(pattern, pathname) {
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -2);
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  }
  if (!pattern.includes(':')) return pattern === pathname;
  const a = pattern.split('/');
  const b = pathname.split('/');
  if (a.length !== b.length) return false;
  return a.every((seg, i) => (seg.startsWith(':') ? b[i].length > 0 : seg === b[i]));
}

/** Returns the registry entry for a pathname, or UNMATCHED. */
export function findRoute(pathname) {
  const p = normalizePath(pathname);
  const exact = ROUTES.find((r) => r.path === p);
  if (exact) return exact;
  const param = ROUTES.find((r) => r.path.includes(':') && matchesPattern(r.path, p));
  if (param) return param;
  const prefix = ROUTES.find((r) => r.path.endsWith('/*') && matchesPattern(r.path, p));
  if (prefix) return prefix;
  return UNMATCHED;
}

export function canonicalUrlFor(path) {
  const p = normalizePath(path);
  return p === '/' ? `${SITE_URL}/` : `${SITE_URL}${p}`;
}

/**
 * Fully resolved, render-ready metadata for a pathname.
 * Indexable entries get a canonical; non-indexable aliases get the canonical
 * of their target page (so /privacy points at /privacy-policy); everything
 * else gets none.
 */
export function resolveRouteMeta(pathname) {
  const p = normalizePath(pathname);
  const entry = findRoute(p);
  const indexable = entry.index === true;
  let canonical = null;
  if (indexable) canonical = canonicalUrlFor(entry.path);
  else if (entry.canonicalPath) canonical = canonicalUrlFor(entry.canonicalPath);

  return {
    ...entry,
    pathname: p,
    indexable,
    canonical,
    url: canonicalUrlFor(p),
  };
}

/** All indexable entries (sitemap + prerender list). */
export function indexableRoutes() {
  return ROUTES.filter((r) => r.index === true);
}

/** Static (non-pattern) paths the SPA must answer with the app shell. */
export function knownStaticPaths() {
  return ROUTES.filter((r) => !r.path.includes(':') && !r.path.endsWith('/*')).map((r) => r.path);
}

/** Prefixes the SPA must answer with the app shell. */
export function knownPrefixes() {
  return ROUTES.filter((r) => r.path.endsWith('/*')).map((r) => r.path.slice(0, -2));
}

/** Param patterns, as "/verify/:token" strings. */
export function knownPatterns() {
  return ROUTES.filter((r) => r.path.includes(':')).map((r) => r.path);
}
